import { ListItem, ListItemText, TextField, Box, Collapse, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ArrowForwardIosSharp } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import './StudentRecordActivities.css';
import { useDataValidations } from '../../../shared/hooks/useDataValidations';
import useRuleValidation from '../../../shared/hooks/useRuleValidation';
import { cleanWhiteSpaces } from '../../../shared/utils/commons';
import Activity, { AcademicTranscript } from '../../../models/Activity'
import { RequestData } from '../useRequestData';
import AppFile from '../../../models/AppFile'

const useIsAcademicTranscriptIsComplete = (item: Activity) => {
  const { isValidApprovalDate } = useDataValidations();
  const condition = [
    item?.academic_transcript?.book,
    item?.academic_transcript?.calification,
    isValidApprovalDate(item?.academic_transcript?.approval_date),
    item?.academic_transcript?.folder
  ].every(i => !!i);
  return condition;
};

interface StudentRecordActivitiesProps {
  item: Activity,
  requestData: RequestData,
  isSourceInstitutionEqualToTargetInstitution?: boolean | null;
}

const StudentRecordActivities: React.FC<StudentRecordActivitiesProps> = ({ item, requestData, isSourceInstitutionEqualToTargetInstitution }) => {
  const { t } = useTranslation();
  const { validateCalification, maxTextLenght, isValidApprovalDate, getMessageYearValid } = useDataValidations();
  const { activities, setActivities } = requestData;
  const [activity, setActivity] = useState(item);
  const [expanded, setExpanded] = useState(true);
  const initialAcademicTranscript = {
    folder: '',
    calification: '',
    book: '',
    approval_date: ''
  };

  const checkAcademicTranscript = activity?.academic_transcript ?? initialAcademicTranscript;
  // inputs validations
  const completedAcademicTranscript = useIsAcademicTranscriptIsComplete(activity ?? initialAcademicTranscript);
  const folderRule = useRuleValidation(() => !!checkAcademicTranscript.folder);
  const calificationRule = useRuleValidation(() => !!checkAcademicTranscript.calification);
  const bookRule = useRuleValidation(() => !!checkAcademicTranscript.book);
  const [isIncompleteDate, setIsIncompleteDate] = useState(false);
  const approvalDateRule = useRuleValidation(() => !!checkAcademicTranscript.approval_date && isValidApprovalDate(checkAcademicTranscript.approval_date));
  const backgroundColor = completedAcademicTranscript ? '#ddffdd' : '#f4f4f4';

  useEffect(() => {
    setActivity(item);
  }, [item]);

  const checkValidDate = useMemo(() => {
    return isIncompleteDate ||
      (
        activity?.academic_transcript?.approval_date !== undefined &&
        activity?.academic_transcript?.approval_date.length !== 0 &&
        !isValidApprovalDate(activity?.academic_transcript?.approval_date)
      );
  }, [isIncompleteDate, activity?.academic_transcript?.approval_date]);

  const updateActivities = (attribute: string, value?: AppFile | AcademicTranscript | null) => {
    const equivalencyId = activity.id;
    const activityToUpdate = activities?.find((a: Activity) => a.id === equivalencyId);
    if (activityToUpdate) {
      const activitiesCopy = [...activities];
      const updatedActivity = {
        ...activityToUpdate,
        ruleMatch: activity.ruleMatch,
        [attribute]: value
      };
      const index = activitiesCopy.findIndex((a: Activity) => a.id === equivalencyId);
      if (index !== -1) {
        activitiesCopy[index] = updatedActivity;
        setActivity(updatedActivity);
        const updatedActivities = [
          ...activitiesCopy.map((a: Activity) => {
            return {
              ...a,
              academic_transcript: {
                ...a.academic_transcript,
                book: cleanWhiteSpaces(a?.academic_transcript?.book),
                folder: cleanWhiteSpaces(a?.academic_transcript?.folder)
              }
            };
          })
        ];
        setActivities(updatedActivities);
      }
    }
  };

  /* UPDATE ACADEMIC TRANSCRIPT */
  const handleInputsChange = (_value: string, _name: string) => {
    const value = _value.trim() ? _value : '';
    const att = _name;

    if (att === 'calification' && !validateCalification(value)) {
      return;
    }

    if ((att === 'book' || att === 'folder') && maxTextLenght(value, 50)) {
      return;
    }
    const newTranscript = {
      ...initialAcademicTranscript,
      ...activity?.academic_transcript,
      [att]: value
    };
    updateActivities('academic_transcript', newTranscript);
  };

  const handleChangeExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Box className="REQXXI-StudentRecordActivities-BoxRow" data-testid="StudentRecordActivities-Container" sx={{ backgroundColor }} >
      <ListItem className='REQXXI-StudentRecordActivities-ListItem' data-testid='clickExpanded-arrow' onClick={handleChangeExpanded}>
        <Grid container className='REQXXI-StudentRecordActivities-titleContainer'>
          <Grid item display={'flex'} direction={'row'} alignItems={'center'}>
            {expanded
              ? <ArrowForwardIosSharp data-testid='expanded-arrow' sx={{ fontSize: '0.9rem', margin: '0 0 0 8px', transform: 'rotate(90deg)' }} />
              : <ArrowForwardIosSharp data-testid='notExpanded-arrow' sx={{ fontSize: '0.9rem', margin: '0 0 0 8px' }} />
            }
            <ListItemText sx={{ marginLeft: '24px' }}>
              {activity?.input?.map(i => i.name).join(', ')}
              {activity?.name}
            </ListItemText>
          </Grid>
        </Grid>
      </ListItem>
      <Collapse in={expanded} timeout={600}>
        <Box sx={{ padding: '0 8px 16px 8px' }}>
          <Box className="REQXXI-StudentRecordActivities-Column">
            <TextField
              disabled={!!isSourceInstitutionEqualToTargetInstitution && !!activity?.academic_transcript?.calification}
              size="small"
              label={t('calification')}
              fullWidth
              className="REQXXI-StudentRecordActivities-RowGrade opacity-disable"
              variant="standard"
              type="number"
              value={activity?.academic_transcript?.calification ?? ''}
              name='calification'
              onChange={(event) => {
                if (/^\d*$/.test(event.target.value)) {
                  handleInputsChange(event.target.value, event.target.name);
                }
              }}
              id="calification-input"
              inputProps={{ 'data-testid': 'calification-input' }}
              InputProps={{
                endAdornment: calificationRule.adornment,
                inputMode: 'numeric'
              }}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
            <TextField
              disabled={!!isSourceInstitutionEqualToTargetInstitution && !!activity?.academic_transcript?.approval_date}
              label={t('finishDate')}
              value={activity?.academic_transcript?.approval_date ?? ''}
              name='approval_date'
              onBlur={(event) => {
                setIsIncompleteDate(event.target.value.length === 0)
              }}
              onChange={(event) => {
                setIsIncompleteDate(event.target.value.length === 0)
                handleInputsChange(event.target.value, event.target.name)
              }}
              helperText={checkValidDate && <div dangerouslySetInnerHTML={{ __html: getMessageYearValid() }} />}
              fullWidth
              className="REQXXI-StudentRecordActivities-RowDate opacity-disable"
              variant="standard"
              type='date'
              id="finishDate-input"
              inputProps={{ 'data-testid': 'finishDate-input', max: '9999-12-31' }}
              InputLabelProps={{
                shrink: true
              }}
              InputProps={{
                endAdornment: approvalDateRule.adornment
              }}
            />
            <TextField
              disabled={!!isSourceInstitutionEqualToTargetInstitution && !!activity?.academic_transcript?.folder}
              label={t('folder')}
              fullWidth
              className="REQXXI-StudentRecordActivities-RowPage opacity-disable"
              variant="standard"
              value={activity?.academic_transcript?.folder ?? ''}
              name='folder'
              onChange={(event) => handleInputsChange(event.target.value, event.target.name)}
              id="folder-input"
              inputProps={{ 'data-testid': 'folder-input' }}
              InputProps={{
                endAdornment: folderRule.adornment
              }}
            />
            <TextField
              disabled={!!isSourceInstitutionEqualToTargetInstitution && !!activity?.academic_transcript?.book}
              label={t('book')}
              fullWidth
              className="REQXXI-StudentRecordActivities-RowBook opacity-disable"
              variant="standard"
              value={activity?.academic_transcript?.book ?? ''}
              name='book'
              onChange={(event) => handleInputsChange(event.target.value, event.target.name)}
              id="book-input"
              inputProps={{ 'data-testid': 'book-input' }}
              sx={{
                '& .MuiInputBase-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
              InputProps={{
                endAdornment: bookRule.adornment
              }}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default StudentRecordActivities;
